// =====================================================
// DataProvider — fuente de datos según el modo
//
//   · demo  -> datos mock (cliente)
//   · real  -> datos de Supabase del usuario autenticado
//
// Mantiene `window.MockData` apuntando al dataset activo para que
// las pantallas (que lo leen de forma dinámica) sigan funcionando,
// y fuerza un re-render con una `version` cuando los datos cambian.
// =====================================================
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { MockData as MOCK } from '../mock/index.js';
import { loadRealData } from './repo.js';
import { useAuth } from '../auth/AuthContext.jsx';

const DataContext = createContext(null);

export function DataProvider({ demoMode = false, children }) {
  const auth = useAuth();
  const [ready, setReady] = useState(demoMode);
  const [version, setVersion] = useState(0);

  // --- modo demo: dataset mock estático ---
  useEffect(() => {
    if (!demoMode) return;
    window.MockData = MOCK;
    setReady(true);
    setVersion((v) => v + 1);
  }, [demoMode]);

  // --- modo real: cargar desde Supabase ---
  const reload = useCallback(async () => {
    if (demoMode || !auth.user) return;
    const ds = await loadRealData({
      user: auth.user,
      profile: auth.profile,
      roles: auth.roles,
    });
    window.MockData = ds;
    setReady(true);
    setVersion((v) => v + 1);
  }, [demoMode, auth.user, auth.profile, auth.roles]);

  useEffect(() => {
    if (demoMode) return;
    if (!auth.user) return;
    let active = true;
    setReady(false);
    loadRealData({ user: auth.user, profile: auth.profile, roles: auth.roles })
      .then((ds) => {
        if (!active) return;
        window.MockData = ds;
        setReady(true);
        setVersion((v) => v + 1);
      })
      .catch((err) => {
        console.error('Error cargando datos reales:', err);
        if (!active) return;
        // Fallback seguro: dataset vacío con estructura válida (sin datos mock)
        window.MockData = {
          USERS: [],
          CURRENT_USER: {
            id: auth.user?.id,
            name: auth.profile?.display_name || auth.user?.email?.split('@')[0] || 'Usuario',
            initials: (auth.profile?.display_name || auth.user?.email || '·').slice(0, 2).toUpperCase(),
            role: 'viewer',
            title: auth.profile?.title || '',
            email: auth.user?.email || '',
            hue: auth.profile?.avatar_hue ?? 200,
          },
          ASSISTANTS: MOCK.ASSISTANTS,
          STATUS_LABELS: MOCK.STATUS_LABELS,
          BIDS: [],
          SANDETEL: null,
          TARIFF: [],
          LIBRARY: { clauses: [], templates: [], memories: [] },
          CALENDAR: [],
          MEMORY_INDEX: [],
          CHAT: { threads: [], messages: [] },
        };
        setReady(true);
        setVersion((v) => v + 1);
      });
    return () => { active = false; };
  }, [demoMode, auth.user, auth.profile, auth.roles]);

  if (!ready) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-0)' }}>
        <span className="skeleton" style={{ width: 28, height: 28, borderRadius: 999 }}></span>
      </div>
    );
  }

  return (
    <DataContext.Provider value={{ demoMode, version, reload }}>
      <React.Fragment key={version}>{children}</React.Fragment>
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
